import type { MutableRefObject, ReactNode } from "react";
import { StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";

import { Chapter } from "./Chapter";
import { ContextAnswerReview } from "./ContextAnswerReview";
import { ContextHintBlock } from "./ContextHints";
import { SpecRow } from "./SpecRow";

import { QuestionRenderer } from "@/components/questions/QuestionRenderer";
import { ValidationErrorCard, type ErrorCardStatus } from "@/components/ValidationErrorCard";
import type { ContextHint, Question, ValidationError } from "@/lib/api";
import {
    evaluateVisibility,
    computeDepths,
    computeRenderOrder,
    type QuestionWithRules
} from "@/lib/branching";
import type { ContextAnswerReview as ReviewItem } from "@/lib/context-hint-review";
import { colors, fonts } from "@/lib/theme";

interface Props {
    questions: Question[];
    answers: Record<string, string>;
    onChange: (questionId: string, value: string) => void;
    readOnly: boolean;
    visitId: string;
    sectionId: string;
    errorsByQuestion: Map<string | null, ValidationError[]>;
    orphanErrors: ValidationError[];
    /** Set of error ids the RN has acknowledged (visually dismissed; DELETE fired). */
    dismissedLocalIds: Set<number>;
    onAddNarrative: (err: ValidationError) => void;
    onAddDiagnosis: (err: ValidationError) => void;
    onAcknowledge: (errorId: number) => void;
    requiredErrors: Set<string>;
    questionYPositions: MutableRefObject<Map<string, number>>;
    questionRefs?: MutableRefObject<Map<string, View>>;
    /** Patient demographics, shown as read-only context by the bodyMetrics card. */
    patient?: { dob: string | null; gender: string | null };
    /**
     * Render the questions without the "Chapter 01 — Questions / Document the
     * visit" header. The patient questionnaire reuses this chapter for its
     * question rendering and branching, but its reader is the patient, not a
     * nurse documenting a visit — the header would be nonsense to them.
     */
    plain?: boolean;
    /**
     * Patient context (what is already known before the question is asked),
     * keyed by question id. Hints render as attributed, dated reports under
     * the control; checks as amber, non-blocking tags. Both are optional and
     * absent in `plain` mode — the patient never sees hints about herself.
     */
    contextHints?: Map<string, ContextHint[]>;
    onOpenContextHint?: (hint: ContextHint) => void;
    answerReviewItems?: ReviewItem[];
    dismissingReview?: boolean;
    onDismissReviewItem?: (key: string) => void;
}

/**
 * Chapter 01 — visible questions stacked. Per-error UI state (which cards
 * have been acknowledged) is threaded down from the orchestrator. The
 * chapter itself is stateless w.r.t. the validation lifecycle — it just
 * renders cards for activeErrors.
 *
 * Validation:
 *   - Errors attached to a question render inside its SpecRow under the control.
 *   - Orphans (no questionId or unmatched) render at the top under "General".
 *   - Required-empty errors paint the SpecRow with a red error line.
 *   - Card flips to "Acknowledged & dismissed" via the status prop derived
 *     from dismissedLocalIds. The inline composer / narrative-saved status
 *     was removed — Add narrative now scrolls down to Chapter 02.
 */
export function QuestionsChapter({
    plain = false,
    questions,
    answers,
    onChange,
    readOnly,
    visitId,
    sectionId,
    errorsByQuestion,
    orphanErrors,
    dismissedLocalIds,
    onAddNarrative,
    onAddDiagnosis,
    onAcknowledge,
    requiredErrors,
    questionYPositions,
    questionRefs,
    patient,
    contextHints,
    onOpenContextHint,
    answerReviewItems,
    dismissingReview,
    onDismissReviewItem
}: Props) {
    const isMobileVisible = (q: Question) => q.visibleOnMobile !== false;
    const rootQuestions = questions.filter((q) => isMobileVisible(q) && q.parentQuestionId == null);

    const questionsWithRules: QuestionWithRules[] = rootQuestions.map((q) => ({
        id: q.id,
        branchingRules: q.branchingRules ?? []
    }));
    const visibleIds = evaluateVisibility(questionsWithRules, answers);
    const depths = computeDepths(questionsWithRules);
    const visibleRoots = rootQuestions.filter((q) => visibleIds.has(q.id));
    // Place each branched question right after its trigger instead of at its
    // raw order_index slot — the ≥ 1000 "park at end" convention otherwise
    // floats branched clusters to the bottom of the section. Visibility is
    // already applied, so hidden branches don't influence placement. See
    // computeRenderOrder in lib/branching.ts.
    const renderOrder = computeRenderOrder(
        visibleRoots.map((q) => ({ id: q.id, branchingRules: q.branchingRules ?? [] }))
    );
    const orderPos = new Map<string, number>(renderOrder.map((id, i) => [id, i]));
    const visibleQuestions = [...visibleRoots]
        .sort((a, b) => (orderPos.get(a.id) ?? 0) - (orderPos.get(b.id) ?? 0))
        // Hidden computed fields (e.g. an auto-filled "status" whose only job is
        // to feed branching) stay in the visibility graph above so they can
        // trigger follow-ups, but are never drawn — the nurse never sees them.
        .filter((q) => (q.config as { hidden?: boolean } | null)?.hidden !== true);

    const childQuestionsByParent = new Map<string, Question[]>();
    for (const q of questions) {
        if (q.parentQuestionId) {
            const list = childQuestionsByParent.get(q.parentQuestionId) ?? [];
            list.push(q);
            childQuestionsByParent.set(q.parentQuestionId, list);
        }
    }

    const renderCard = (err: ValidationError, key: string): ReactNode => (
        <ValidationErrorCardWrapper
            key={key}
            error={err}
            dismissedLocalIds={dismissedLocalIds}
            onAddNarrative={onAddNarrative}
            onAddDiagnosis={onAddDiagnosis}
            onAcknowledge={onAcknowledge}
        />
    );

    const body = (
        <View style={styles.column}>
            {orphanErrors.length > 0 ? (
                <View>
                    <Text style={styles.generalLabel}>General</Text>
                    {orphanErrors.map((err) => renderCard(err, `orphan-${err.id ?? err.message}`))}
                </View>
            ) : null}

            {visibleQuestions.map((question) => {
                const qid = String(question.id);
                const qErrors = errorsByQuestion.get(qid) ?? [];
                const requiredErr = requiredErrors.has(qid) ? "This field is required." : null;
                // The card paints red when ANY attached error is still
                // unresolved (active — not in dismissedLocalIds, or has
                // no id so dismissal isn't tracked yet).
                const hasActiveError = qErrors.some(
                    (e) => e.id == null || !dismissedLocalIds.has(e.id)
                );
                return (
                    <QuestionSlot
                        key={question.id}
                        qid={qid}
                        question={question}
                        depth={depths.get(question.id) ?? 0}
                        value={answers[question.id]}
                        onChange={onChange}
                        readOnly={readOnly}
                        visitId={visitId}
                        sectionId={sectionId}
                        childQuestions={childQuestionsByParent.get(question.id)}
                        childAnswers={answers}
                        errors={qErrors}
                        requiredError={requiredErr}
                        flagged={hasActiveError || requiredErr != null}
                        renderErrorCard={renderCard}
                        questionYPositions={questionYPositions}
                        questionRefs={questionRefs}
                        patient={patient}
                        contextHints={plain ? undefined : contextHints?.get(qid)}
                        onOpenContextHint={onOpenContextHint}
                        answerReviewItems={
                            plain
                                ? undefined
                                : answerReviewItems?.filter((item) => item.questionId === qid)
                        }
                        dismissingReview={dismissingReview}
                        onDismissReviewItem={onDismissReviewItem}
                    />
                );
            })}

            {visibleQuestions.length === 0 ? (
                <Text style={styles.empty}>No questions in this section</Text>
            ) : null}
        </View>
    );

    if (plain) {
        return <View style={styles.plain}>{body}</View>;
    }
    return (
        <Chapter num="01" title="Questions" helper="Document the visit.">
            {body}
        </Chapter>
    );
}

interface QuestionSlotProps {
    qid: string;
    question: Question;
    depth: number;
    patient?: { dob: string | null; gender: string | null };
    value: string | undefined;
    onChange: (questionId: string, value: string) => void;
    readOnly: boolean;
    visitId: string;
    sectionId: string;
    childQuestions: Question[] | undefined;
    childAnswers: Record<string, string>;
    errors: ValidationError[];
    requiredError: string | null;
    flagged: boolean;
    renderErrorCard: (err: ValidationError, key: string) => ReactNode;
    questionYPositions: MutableRefObject<Map<string, number>>;
    questionRefs?: MutableRefObject<Map<string, View>>;
    contextHints?: ContextHint[];
    onOpenContextHint?: (hint: ContextHint) => void;
    answerReviewItems?: ReviewItem[];
    dismissingReview?: boolean;
    onDismissReviewItem?: (key: string) => void;
}

function QuestionSlot({
    qid,
    question,
    depth,
    value,
    onChange,
    readOnly,
    visitId,
    sectionId,
    patient,
    childQuestions,
    childAnswers,
    errors,
    requiredError,
    flagged,
    renderErrorCard,
    questionYPositions,
    questionRefs,
    contextHints,
    onOpenContextHint,
    answerReviewItems,
    dismissingReview,
    onDismissReviewItem
}: QuestionSlotProps) {
    return (
        <View
            ref={(node) => {
                if (node) {
                    questionRefs?.current.set(qid, node);
                } else {
                    questionRefs?.current.delete(qid);
                }
            }}
            nativeID={`context-question-${qid}`}
            collapsable={false}
            style={{ marginLeft: depth * 16 }}
            onLayout={(e) => {
                questionYPositions.current.set(qid, e.nativeEvent.layout.y);
            }}
        >
            <SpecRow
                label={question.text}
                hint={question.hintText ?? undefined}
                required={question.mandatory}
                error={requiredError}
                flagged={flagged}
                errorSlot={
                    errors.length > 0
                        ? errors.map((err) => renderErrorCard(err, `err-${err.id ?? err.message}`))
                        : null
                }
            >
                <QuestionRenderer
                    bare
                    question={question}
                    value={value}
                    onChange={(v) => onChange(qid, v)}
                    readOnly={readOnly}
                    visitId={visitId}
                    sectionId={sectionId}
                    patient={patient}
                    depth={depth}
                    childQuestions={childQuestions}
                    childAnswers={childAnswers}
                    onChildAnswerChange={(childId, v) => onChange(childId, v)}
                />
                {answerReviewItems?.length ? (
                    <ContextAnswerReview
                        inline
                        items={answerReviewItems}
                        saving={dismissingReview ?? false}
                        onDismiss={() => {}}
                        onDismissItem={onDismissReviewItem}
                        onSource={(hint) => onOpenContextHint?.(hint)}
                    />
                ) : null}
                {contextHints && contextHints.length > 0 ? (
                    <ContextHintBlock
                        hints={contextHints}
                        answer={value}
                        options={question.answerOptions}
                        onOpen={(hint) => onOpenContextHint?.(hint)}
                    />
                ) : null}
            </SpecRow>
        </View>
    );
}

interface ValidationErrorCardWrapperProps {
    error: ValidationError;
    dismissedLocalIds: Set<number>;
    onAddNarrative: (err: ValidationError) => void;
    onAddDiagnosis: (err: ValidationError) => void;
    onAcknowledge: (errorId: number) => void;
}

export function ValidationErrorCardWrapper({
    error,
    dismissedLocalIds,
    onAddNarrative,
    onAddDiagnosis,
    onAcknowledge
}: ValidationErrorCardWrapperProps) {
    const id = error.id ?? -1;
    const status: ErrorCardStatus = dismissedLocalIds.has(id) ? "dismissed" : "active";
    return (
        <View style={styles.errorSlot}>
            <ValidationErrorCard
                error={error}
                status={status}
                onAddNarrative={() => onAddNarrative(error)}
                onAddDiagnosis={() => onAddDiagnosis(error)}
                onAcknowledge={() => onAcknowledge(id)}
            />
        </View>
    );
}

const styles = StyleSheet.create({
    column: {
        gap: 26
    },
    plain: {
        // The Chapter wrapper normally supplies the gutter; stand in for it.
        paddingHorizontal: 20,
        paddingTop: 8,
        paddingBottom: 12
    },
    generalLabel: {
        fontFamily: fonts.extraBold,
        fontSize: 10,
        color: colors.textTertiary,
        letterSpacing: 1,
        textTransform: "uppercase",
        marginBottom: 6
    },
    errorSlot: {
        marginTop: 0
    },
    empty: {
        fontFamily: fonts.medium,
        fontSize: 14,
        color: colors.textTertiary,
        fontStyle: "italic",
        textAlign: "center",
        marginTop: 32
    }
});
